import React from 'react';
import styled from 'styled-components';
import {VerticalSpacer} from '../../spacer';
import {Typography} from '../../typography';
import {Eyebrow} from '../../eyebrow';
import {AccentColorOptions} from 'epic-ue-theme';
import {BgImage, Image} from '../../images';
import {GridContainer} from '../../grid';
import {LineTextBlock} from '../../text';
import Grid, {GridSize} from '@material-ui/core/Grid';
import {TextLink} from '../../links';
import {Button} from '../../buttons';
import {AnimateOnViewWrap, WithCaption, WithCaptionProps} from '../../../wraps';

const getRadialGradient = (from, to) =>
    `radial-gradient(100% 185.14% at 100% 100%, ${from} 0%, ${to} 100%)`;

const ProductPromoWrapper = styled.div`
    && {
        //makes up for negative margin set by ProductLogoContainer
        position: relative;
        top: 40px;

        .grid-product-promo-item {
            position: relative;
        }

        ${(p) => p.theme.breakpoints.down('sm')} {
            .grid-product-promo-item.hasLogo {
                &:not(:first-of-type) {
                    margin-top: 80px;
                }
            }
        }

        .image-wrapper {
            position: absolute;
            top: 0;
            left: 0;
        }
    }

    .tinyCaption,
    .overlay-caption-wrapper {
        text-align: center;
    }
`;

const Styled = styled.div<{oneCard: boolean; hasCaption?: string}>`
    && {
        position: relative;
        padding: 40px;
        text-align: center;
        background: linear-gradient(
            360deg,
            ${(p) => p.theme.palette.neutral.charcoal} 0%,
            rgba(13, 16, 35, 0.5) 100%
        );
        display: flex;
        justify-content: center;
        height: 100%;
        box-sizing: border-box;
        color: ${(p) => p.theme.palette.common.white};
        ${(p) => p.theme.breakpoints.down('sm')} {
            padding: 16px;
        }
    }
    .heading.h2 ${(p) => p.theme.breakpoints.down('sm')} {
        font-size: 30px;
        line-height: 38px;
    }
    .ue-link-wrap {
        color: ${(p) => p.theme.palette.common.white};
        margin-top: auto;
        &.ue-outlined:focus-visible {
            outline: 1px solid ${(p) => p.theme.palette.neutral.pearl};
            & > .heading {
                outline: 1px solid ${(p) => p.theme.palette.neutral.pearl};
            }
        }
    }
    .ue-link-wrap-span {
        display: contents;
    }
    .ue-product-promo {
        display: flex;
        flex-flow: column nowrap;
        width: 100%;
        overflow: hidden;
        border: 1px solid ${(p) => p.theme.palette.common.white};
        padding: 96px 56px ${(p) => (p.oneCard ? 96 : 40)}px 56px;
        ${(p) => p.theme.breakpoints.down('sm')} {
            padding: 72px 24px 48px 24px;
        }
        .paragraph {
            color: ${(p) => p.theme.palette.common.white};
        }
    }

    ${(p) => p.hasCaption && `.ue-link-wrap { margin-top: 0;}`};
`;

const ProductLogoContainer = styled.div`
    &&& {
        width: 120px;
        height: 120px;
        position: absolute;
        top: -40px;
        left: calc(50% - 60px);
        ${(p) => p.theme.breakpoints.down('sm')} {
            width: 80px;
            height: 80px;
            top: -40px;
            left: calc(50% - 40px);
        }
        .background-image {
            background-size: cover;
            min-height: auto;
            min-width: auto;
            height: 50%;
            width: 50%;
            opacity: 1;
        }
    }
`;

const ProductLogo = styled.div<{accentColor: AccentColorOptions; solidColor?: string}>`
    background: ${(p) =>
        p.solidColor
            ? p.solidColor
            : getRadialGradient(
                  p.theme.palette.gradient[p.accentColor].from,
                  p.theme.palette.gradient[p.accentColor].to
              )};

    width: 100%;
    height: 100%;
    position: relative;
    div {
        width: 75%;
        height: 75%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;

const PromoImgCaption = styled.div`
    display: contents;
    .with-caption {
        margin-top: auto;
    }
`;

export interface Product {
    eyebrowText?: string;
    title?: string;
    description?: string;
    buttonText?: string;
    bgImageUrl: string;
    bgImageAlt?: string;
    productLogo?: ProductLogo;
    href?: string;
    caption?: WithCaptionProps;
}

export interface ProductPromoSectionProps {
    products?: Product[];
    headline?: string;
    accentColor?: AccentColorOptions;
    solidColor?: string;
}

export interface ProductLogo {
    imageUrl?: string;
    accentColor?: AccentColorOptions;
}

export const ProductPromoSection = ({
    products = [],
    headline,
    accentColor,
    solidColor
}: ProductPromoSectionProps): JSX.Element => {
    const md = (12 / products.length) as GridSize;
    //textGridSizeLG: Text spans 6 grid columns (for single product variant) on LG,XL and 12 othwerwise
    const textGridSizeLG = products.length > 1 ? 12 : 6;
    const defaultLogoColors: AccentColorOptions[] = ['sky', 'forest'];
    return (
        <ProductPromoWrapper>
            <AnimateOnViewWrap translateY={30}>
            <GridContainer>
                {headline && (
                    <>
                        <LineTextBlock headline={headline} />
                        <VerticalSpacer variant="custom" spacing={10} downSmSpacing={7} />
                    </>
                )}
                <VerticalSpacer variant="custom" spacing={0} downSmSpacing={3} />
                <Grid container>
                    {products.map(
                        (
                            {
                                eyebrowText,
                                title,
                                description,
                                buttonText = 'Learn more',
                                bgImageUrl,
                                bgImageAlt,
                                href,
                                productLogo,
                                caption
                            },
                            index
                        ) => (
                            <React.Fragment key={index}>
                                <Grid
                                    item
                                    md={md}
                                    sm={12}
                                    xs={12}
                                    className={`grid-product-promo-item ${
                                        productLogo?.imageUrl && 'hasLogo'
                                    }`}>
                                    <Image src={bgImageUrl} disableRatioHelper alt={bgImageAlt} />
                                    <Styled
                                        oneCard={products.length === 1}
                                        hasCaption={caption?.text}>
                                        {productLogo && productLogo.imageUrl && (
                                            <ProductLogoContainer>
                                                <ProductLogo
                                                    accentColor={
                                                        productLogo.accentColor ||
                                                        defaultLogoColors[index]
                                                    }
                                                    solidColor={solidColor}>
                                                    <BgImage img={productLogo.imageUrl} />
                                                </ProductLogo>
                                            </ProductLogoContainer>
                                        )}
                                        <div className="ue-product-promo">
                                            <Eyebrow>
                                                <Typography variant="h4" big>
                                                    {eyebrowText}
                                                </Typography>
                                            </Eyebrow>
                                            <VerticalSpacer
                                                variant="custom"
                                                spacing={9}
                                                downSmSpacing={3}
                                            />
                                            <Grid
                                                container
                                                direction="row"
                                                justifyContent="center"
                                                alignItems="center">
                                                <Grid item lg={textGridSizeLG}>
                                                    <TextLink
                                                        href={href}
                                                        accentColor={accentColor}
                                                        accentVariant={'bright'}>
                                                        <Typography variant="h2">
                                                            {title}
                                                        </Typography>
                                                    </TextLink>
                                                    <VerticalSpacer variant="custom" spacing={2} />
                                                    <Typography variant="p">
                                                        {description}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            <VerticalSpacer
                                                variant="custom"
                                                spacing={4}
                                                downSmSpacing={3}
                                            />
                                            {caption && (
                                                <PromoImgCaption>
                                                    <WithCaption {...caption}></WithCaption>
                                                    <VerticalSpacer variant="custom" spacing={1} />
                                                </PromoImgCaption>
                                            )}
                                            <Button
                                                href={href}
                                                color={accentColor}
                                                type="secondary">
                                                {buttonText}
                                            </Button>
                                        </div>
                                    </Styled>
                                </Grid>
                            </React.Fragment>
                        )
                    )}
                </Grid>
            </GridContainer>
            </AnimateOnViewWrap>
        </ProductPromoWrapper>
    );
};
